import { Component, inject, OnInit, OnDestroy, signal, ChangeDetectorRef, computed } from "@angular/core";
import { CommonModule } from "@angular/common";
import { HttpClient } from "@angular/common/http";
import { MatButtonModule } from "@angular/material/button";
import { MatCardModule } from "@angular/material/card";
import { MatIconModule } from "@angular/material/icon";
import { MatSnackBarModule } from "@angular/material/snack-bar";
import { Subject, takeUntil, switchMap, catchError, of, combineLatest, map, take, distinctUntilChanged, debounceTime } from "rxjs";
import { SessionStorage } from "@dffedb/util";
import { ToastService } from "@dffedb/ui";

import { PushNotificationService } from "./push-notification.service";
import { AktuelBrugerObserver, Rolle } from "@e-forsyning/common/bruger";
import { AktuelInstallationKeyObserver, AktuelInstallationObserver } from "@e-forsyning/common/installation";
import { UmbracoIndstillingerObserver } from "@e-forsyning/common/umbraco-indstillinger";

@Component({
    selector: "eforsyning-notification-permission-request",
    standalone: true,
    imports: [CommonModule, MatButtonModule, MatCardModule, MatIconModule, MatSnackBarModule],
    templateUrl: "./notification-permission-request.component.html",
    styleUrls: ["./notification-permission-request.component.scss"]
})
export class NotificationPermissionRequestComponent implements OnInit, OnDestroy {
    private readonly toastService = inject(ToastService);
    private readonly pushNotificationService = inject(PushNotificationService);
    private readonly http = inject(HttpClient);
    private readonly aktuelBrugerObserver = inject(AktuelBrugerObserver);
    private readonly aktuelInstallationKeyObserver = inject(AktuelInstallationKeyObserver);
    private readonly aktuelInstallationObserver = inject(AktuelInstallationObserver);
    private readonly umbracoIndstillingerObserver = inject(UmbracoIndstillingerObserver);
    private readonly sessionStorage = inject(SessionStorage);
    private readonly cdr = inject(ChangeDetectorRef);

    private readonly destroy$ = new Subject<void>();

    public isLoading = signal(false);
    public hasRequestedPermission = signal(false);
    public hasDismissed = signal(false);
    public shouldShowSession = signal(false); // Signal til at holde styr på om vi skal vise i denne session
    public notificationsEnabledOnServer = signal<boolean | null>(null); // Signal til at holde styr på om notifikationer er aktiveret på appserveren
    public isUserLoggedIn = signal(false); // Signal til at holde styr på om brugeren er logget ind
    public shouldOverwriteExistingSubscription = signal(false);
    public statusReady = signal(false);
    public statusCheckInProgress = signal(false);
    private isFofBruger = signal(false);
    private isPilotKunde = signal(false);

    // Signal til at holde styr på om permission request skal vises
    // Bruger computed signal så den kun re-evalueres når de underliggende værdier ændres
    public shouldShowPermissionRequest = computed(() => {
        // Tjek om brugeren er logget ind - vis IKKE popup hvis brugeren ikke er logget ind
        if (!this.isUserLoggedIn()) {
            return false;
        }

        // FOF-brugere/teknikere skal IKKE subscribes til notifikationer - spring permission request over
        if (this.isFofBruger()) {
            return false;
        }

        if (!this.isPilotKunde()) {
            return false;
        }

        // VIGTIGT: Vis kun permission request hvis vi kan bruge Capacitor push
        const canUsePushNotifications = this.pushNotificationService.canUsePushNotifications();

        // Vis kun hvis notifikationer er tilladt (Capacitor eller Windows PWA)
        // I browser/PWA mode skal permission request IKKE vises (kun i native apps eller Windows PWA)
        if (!canUsePushNotifications) {
            // Hvis notifikationer ikke er tilladt, vis IKKE permission request i browser
            return false;
        }

        // Tjek om push notifications kan virke (Capacitor)
        if (!this.pushNotificationService.canUsePushNotifications()) {
            return false;
        }

        // Vis kun hvis:
        // 1. Brugeren er logget ind (allerede tjekket ovenfor)
        // 2. Push notifications kan virke (Capacitor) - allerede tjekket ovenfor
        // 3. Vi HAR fundet ud af subscription-status (ikke null = venter på API)
        // 4. Notifikationer IKKE allerede er aktiveret på appserveren - vis ALDRIG hvis bruger er subscribed
        // 5. Vi ikke allerede har anmodet om tilladelse
        // 6. Brugeren ikke har permanent afvist anmodningen
        // 7. Vi har besluttet at vise anmodningen i denne session
        //
        // VIGTIGT: Popup må ALDRIG vises før vi ved om brugeren allerede er subscribed.
        // VIGTIGT: I native Capacitor app skal vi IKKE tjekke browser Notification API
        const hasRequested = this.hasRequestedPermission();
        const hasDismissed = this.hasPermanentlyDismissed();
        const shouldShowSessionValue = this.shouldShowSession();
        const isStatusReady = this.statusReady();
        const enabledOnServer = this.notificationsEnabledOnServer();
        const isStatusCheckInProgress = this.statusCheckInProgress();

        // Aldrig vis popup mens status-check kører, hvis status er ukendt (null),
        // eller når brugeren allerede er subscribed.
        if (isStatusCheckInProgress || enabledOnServer === null || enabledOnServer === true) {
            return false;
        }

        return isStatusReady && !hasRequested && !hasDismissed && shouldShowSessionValue;
    });

    // Tilføj property til at holde styr på tidligere tilladelse status
    private wasPermissionGranted = (() => {
        if (typeof window !== "undefined" && "Notification" in window) {
            try {
                return Notification.permission === "granted";
            } catch (error) {
                return false;
            }
        }
        return false;
    })();
    private isCreatingSubscription = false; // Flag til at forhindre dobbelt kald
    private subscriptionTimeoutHandle: ReturnType<typeof setTimeout> | null = null;
    private currentInstallationKey: string | null = null;
    private userInitiatedSubscriptionRequest = false;

    ngOnInit(): void {
        // Tjek om brugeren er logget ind og har en installation
        this.aktuelBrugerObserver.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(async (bruger) => {
            this.isUserLoggedIn.set(!!bruger);
            this.isFofBruger.set(this.erLoggetIndSomFofBruger());

            // Hvis brugeren logger ud, nulstil state så næste bruger får korrekt opførsel
            // Ryd bruger-specifik subscription metadata - forhindrer forkert popup når bruger B logger ind efter A
            if (!bruger) {
                this.pushNotificationService.clearUserSpecificSubscriptionMetadataOnLogout();
                this.shouldShowSession.set(false);
                sessionStorage.removeItem("notification-permission-shown");
                this.resetPendingSubscriptionState();
                this.statusReady.set(false);
                this.statusCheckInProgress.set(false);
                this.notificationsEnabledOnServer.set(null);
            } else if (this.erLoggetIndSomFofBruger()) {
                // FOF-brugere/teknikere - spring notifikations-subscription over (bruger logged-in rolle fra session, ikke installation-kontekst)
                this.shouldShowSession.set(false);
                sessionStorage.removeItem("notification-permission-shown");
                this.statusReady.set(true);
                this.statusCheckInProgress.set(false);
                this.notificationsEnabledOnServer.set(null);
            }
        });

        // Opret combineLatest subscription kun én gang i ngOnInit
        // Dette sikrer at vi kun har én subscription og undgår duplikerede API kald
        combineLatest([this.aktuelBrugerObserver.valueChanges, this.aktuelInstallationKeyObserver.valueChanges, this.umbracoIndstillingerObserver.valueChanges])
            .pipe(
                takeUntil(this.destroy$),
                debounceTime(200), // Debounce for at undgå flere kald og popup-flicker når installation ændres hurtigt
                distinctUntilChanged((prev, curr) => {
                    // Sammenlign bruger rolle, installation key og appServerUrl for at undgå duplikerede kald
                    const prevBruger = prev[0];
                    const currBruger = curr[0];
                    const prevKey = prev[1]?.ejendomNr;
                    const currKey = curr[1]?.ejendomNr;
                    const prevUrl = prev[2]?.aktuelEforsyning?.appServerUrl;
                    const currUrl = curr[2]?.aktuelEforsyning?.appServerUrl;
                    return prevBruger?.rolle === currBruger?.rolle && prevKey === currKey && prevUrl === currUrl;
                }),
                switchMap(([bruger, installationKey, umbracoIndstillinger]) => {
                    if (bruger && this.erLoggetIndSomFofBruger()) {
                        return of(null);
                    }

                    if (!installationKey || !umbracoIndstillinger?.aktuelEforsyning) {
                        return of(null);
                    }

                    const appServerUrl = umbracoIndstillinger.aktuelEforsyning.appServerUrl;
                    if (!appServerUrl) {
                        return of(null);
                    }

                    const currentInstallationKey = `${appServerUrl}::${installationKey.ejendomNr}`;
                    this.updateCurrentInstallationKey(currentInstallationKey);

                    // VIGTIGT: Sæt statusReady=false så popup IKKE vises før vi har API-svar.
                    this.statusReady.set(false);
                    this.statusCheckInProgress.set(true);
                    this.shouldShowSession.set(false);
                    this.notificationsEnabledOnServer.set(null);

                    const localSubscription = this.pushNotificationService.getCurrentSubscription();
                    const subscriptionOwnerKey = this.getSubscriptionOwnerKey();
                    const isDifferentOwner = !!localSubscription && subscriptionOwnerKey !== currentInstallationKey;

                    const canUsePushNotifications = this.pushNotificationService.canUsePushNotifications();
                    if (!canUsePushNotifications) {
                        return of(null);
                    }

                    return this.pushNotificationService.checkNotificationStatusOnAppServer(appServerUrl).pipe(
                        map((status) => ({ status, appServerUrl, isDifferentOwner, fromError: false })),
                        catchError((error) => {
                            console.error("❌ Fejl ved tjek af notifikationsstatus:", error);
                            // Ved fejl - vis IKKE popup da vi ikke ved om brugeren er subscribed
                            return of({ status: { enabled: false }, appServerUrl, isDifferentOwner, fromError: true });
                        })
                    );
                })
            )
            .subscribe({
                next: (result) => {
                    this.statusCheckInProgress.set(false);
                    if (!result) {
                        this.notificationsEnabledOnServer.set(false);
                        this.statusReady.set(true);
                        this.shouldShowSession.set(false);
                        this.shouldOverwriteExistingSubscription.set(false);
                        return;
                    }

                    const { status, appServerUrl, isDifferentOwner, fromError } = result;
                    this.notificationsEnabledOnServer.set(status.enabled);
                    this.statusReady.set(true);

                    // Ved API-fejl: vis ALDRIG popup - vi ved ikke om brugeren er subscribed
                    if (fromError) {
                        this.shouldShowSession.set(false);
                        return;
                    }
                    const responseUserId = (status as { enabled: boolean; userId?: string }).userId;
                    if (responseUserId) {
                        localStorage.setItem("push-subscription-user-id", responseUserId);
                    }

                    // Tjek browser permission (uanset om appserver har subscription eller ej)
                    // VIGTIGT: I native Capacitor app er Notification API ikke tilgængelig
                    let browserPermission: NotificationPermission | null = null;
                    if (typeof window !== "undefined" && "Notification" in window) {
                        try {
                            browserPermission = Notification.permission;
                        } catch (error) {
                            browserPermission = null;
                        }
                    }

                    const shouldPrompt = !this.hasPermanentlyDismissed() && (isDifferentOwner || status.enabled === false);

                    this.shouldOverwriteExistingSubscription.set(isDifferentOwner);

                    if (shouldPrompt) {
                        this.hasRequestedPermission.set(false);
                        sessionStorage.setItem("notification-permission-shown", "true");
                        this.shouldShowSession.set(true);
                        this.cdr.detectChanges();
                    } else {
                        // Bruger har allerede notifikationer - sæt hasRequestedPermission så popup ikke vises ved race conditions
                        this.hasRequestedPermission.set(true);
                        sessionStorage.removeItem("notification-permission-shown");
                        this.shouldShowSession.set(false);
                        this.cdr.detectChanges();
                    }

                    // Appserver har subscription - tjek om browser permission er korrekt
                    if (status.enabled && browserPermission === "denied") {
                        const userId = (status as { enabled: boolean; userId?: string }).userId || this.pushNotificationService.getCurrentUserId();
                        if (userId && appServerUrl) {
                            this.pushNotificationService
                                .unregisterSubscriptionFromAppServer(appServerUrl, userId)
                                .pipe(takeUntil(this.destroy$))
                                .subscribe({
                                    next: () => {
                                        this.notificationsEnabledOnServer.set(false);
                                        // Nulstil hasRequestedPermission så permission request kan vises igen
                                        this.hasRequestedPermission.set(false);
                                    },
                                    error: (error) => {
                                        console.error("❌ Fejl ved fjernelse af subscription:", error);
                                    }
                                });
                        }
                    }
                },
                error: (error) => {
                    console.error("❌ Fejl ved tjek af notifikationsstatus:", error);
                    this.statusCheckInProgress.set(false);
                    this.notificationsEnabledOnServer.set(false);
                }
            });

        // Start overvågning af browser notifikations-tilladelse
        this.monitorNotificationPermissionChanges();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    /**
     * Tjekker om appen kører i PWA mode
     * På iOS/iPad bruges navigator.standalone, på Android/Chrome bruges display-mode: standalone
     */
    private isPWA(): boolean {
        if (typeof window === "undefined") {
            return false;
        }
        const isStandaloneMedia = window.matchMedia("(display-mode: standalone)").matches;
        const isStandaloneNavigator = (window.navigator as any).standalone === true;
        const isPWA = isStandaloneMedia || isStandaloneNavigator;

        return isPWA;
    }

    private monitorNotificationPermissionChanges(): void {
        // VIGTIGT: Monitor kun permission changes hvis push kan bruges (Capacitor)
        const canUsePushNotifications = this.pushNotificationService.canUsePushNotifications();
        if (!canUsePushNotifications) {
            return;
        }

        // Tjek om push notifications kan virke (Capacitor)
        if (!this.pushNotificationService.canUsePushNotifications()) {
            return;
        }

        // For Capacitor apps, permission håndteres via Capacitor API
        // Vi behøver ikke at monitorere browser permission changes for Capacitor
        return;
    }

    private removeServerSubscription(): void {
        const userId = this.pushNotificationService.getCurrentUserId();
        if (!userId) {
            return;
        }

        // Fjern subscription via app server (samme flow som ved subscribe)
        this.umbracoIndstillingerObserver.valueChanges.pipe(take(1), takeUntil(this.destroy$)).subscribe((umbracoIndstillinger) => {
            const appServerUrl = umbracoIndstillinger?.aktuelEforsyning?.appServerUrl;
            if (!appServerUrl) {
                return;
            }
            this.pushNotificationService
                .unregisterSubscriptionFromAppServer(appServerUrl, userId)
                .pipe(
                    takeUntil(this.destroy$),
                    catchError((error) => {
                        console.error("Fejl ved fjernelse af notifikations-subscription:", error);
                        return of(undefined);
                    })
                )
                .subscribe({
                    next: () => {
                        this.toastService.visInfo("Notifikationer er nu deaktiveret");
                    }
                });
        });
    }

    private hasPermanentlyDismissed(): boolean {
        return localStorage.getItem(this.getPermanentDismissalKey()) === "true";
    }

    private shouldShowThisSession(): boolean {
        return this.shouldShowSession();
    }

    private updateShouldShowSession(): void {
        const shouldShow = sessionStorage.getItem("notification-permission-shown") === "true";
        this.shouldShowSession.set(shouldShow);
    }

    private updateCurrentInstallationKey(currentInstallationKey: string): void {
        this.currentInstallationKey = currentInstallationKey;
    }

    // Metoden checkNotificationStatusForInstallation() er nu flyttet til ngOnInit() for at undgå duplikerede subscriptions
    // Den gamle metode er fjernet da logikken nu er i ngOnInit() combineLatest subscription

    private checkAndShowPermissionRequest(): void {
        // Tjek PWA mode først
        if (!this.isPWA()) {
            return;
        }

        // Tjek om Notification API er tilgængelig
        if (typeof window === "undefined" || !("Notification" in window)) {
            return;
        }

        // Tjek om brugeren allerede har tilladelse
        // VIGTIGT: I native Capacitor app er Notification API ikke tilgængelig
        if (typeof window !== "undefined" && "Notification" in window) {
            try {
                if (Notification.permission === "granted") {
                    return;
                }
            } catch (error) {}
        }

        // Tjek om brugeren tidligere har permanent afvist
        if (this.hasPermanentlyDismissed()) {
            return;
        }

        // Tjek om vi allerede har vist anmodningen i denne session
        if (this.shouldShowThisSession()) {
            return;
        }

        // Marker at vi har vist anmodningen i denne session
        sessionStorage.setItem("notification-permission-shown", "true");
        this.shouldShowSession.set(true);

        // For PWA mode kan Notification.requestPermission() kaldes automatisk
        if (this.isPWA()) {
            // Vent lidt for at sikre at UI er klar, derefter anmod om tilladelse
            setTimeout(() => {
                this.requestPermission();
            }, 1000);
        }
    }

    public requestPermission(): void {
        this.userInitiatedSubscriptionRequest = true;
        this.isLoading.set(true);
        this.hasRequestedPermission.set(true);

        // Skjul popup'en med det samme når brugeren klikker "Godkend"
        sessionStorage.removeItem("notification-permission-shown");
        this.shouldShowSession.set(false);
        this.cdr.detectChanges();

        // VIGTIGT: I native Capacitor app skal vi IKKE bruge browser Notification API
        // Vi bruger Capacitor Push Notifications API i stedet
        const canUsePushNotifications = this.pushNotificationService.canUsePushNotifications();
        const isCapacitor = this.pushNotificationService.isCapacitor();

        if (canUsePushNotifications && isCapacitor) {
            // Native app - brug Capacitor Push Notifications API
            // createServerSubscription kalder automatisk requestNotificationPermissionAndRegisterOnAppServer
            // som bruger Capacitor PushNotifications.requestPermissions()
            this.createServerSubscription(true);
            return;
        }

        // PWA/Browser mode (inkl. Windows PWA) - brug browser Notification API
        // Tjek om permission allerede er granted
        // VIGTIGT: I native Capacitor app er Notification API ikke tilgængelig
        if (typeof window !== "undefined" && "Notification" in window) {
            try {
                if (Notification.permission === "granted") {
                    // Hvis permission allerede er granted, opret subscription direkte uden at spørge om tilladelse igen
                    this.createServerSubscription(true);
                    return;
                }
            } catch (error) {}
        }

        // Først anmod om browser tilladelse (kun hvis permission er "default")
        if ("Notification" in window) {
            Notification.requestPermission()
                .then((permission) => {
                    if (permission === "granted") {
                        // Browser tilladelse givet - opret subscription på server
                        this.createServerSubscription(true);
                    } else if (permission === "denied") {
                        // Browser tilladelse afvist - bloker notifikationer på server
                        this.createServerSubscription(false);
                    } else {
                        // Default state - nulstil loading og vis popup igen hvis brugeren ikke har afvist
                        this.isLoading.set(false);
                        this.hasRequestedPermission.set(false);
                        // Vis popup igen hvis brugeren ikke har permanent afvist
                        // hasDismissed() er ikke længere relevant da brugeren skal vælge enten "Tillad" eller "Slå fra permanent"
                        if (!this.hasPermanentlyDismissed()) {
                            sessionStorage.setItem("notification-permission-shown", "true");
                            this.shouldShowSession.set(true);
                            this.cdr.detectChanges();
                        }
                    }
                })
                .catch((error) => {
                    console.error("Fejl ved anmodning om browser tilladelse:", error);
                    this.isLoading.set(false);
                    this.hasRequestedPermission.set(false);
                    // Vis popup igen hvis brugeren ikke har permanent afvist
                    if (!this.hasPermanentlyDismissed() && !this.hasDismissed()) {
                        sessionStorage.setItem("notification-permission-shown", "true");
                        this.shouldShowSession.set(true);
                        this.cdr.detectChanges();
                    }
                    this.toastService.visFejl("Der opstod en fejl ved anmodning om notifikations-tilladelse");
                });
        } else {
            this.toastService.visFejl("Denne browser understøtter ikke notifikationer");
            this.isLoading.set(false);
            this.hasRequestedPermission.set(false);
            // Vis popup igen hvis browser ikke understøtter notifikationer
            if (!this.hasPermanentlyDismissed() && !this.hasDismissed()) {
                sessionStorage.setItem("notification-permission-shown", "true");
                this.shouldShowSession.set(true);
                this.cdr.detectChanges();
            }
        }
    }

    /**
     * Aktiverer notifikationer automatisk på appserveren når permission er "granted" og der er en lokal subscription
     */
    private activateNotificationsOnServer(appServerUrl: string): void {
        // Forhindre dobbelt kald
        if (this.isCreatingSubscription) {
            return;
        }

        this.isCreatingSubscription = true;

        // Hent forbrugernumre fra aktuel installation
        combineLatest([this.aktuelInstallationKeyObserver.valueChanges, this.umbracoIndstillingerObserver.valueChanges])
            .pipe(
                takeUntil(this.destroy$),
                take(1),
                switchMap(([installationKey, umbracoIndstillinger]) => {
                    if (!installationKey || !umbracoIndstillinger?.aktuelEforsyning) {
                        this.isCreatingSubscription = false;
                        return of(null);
                    }

                    const forbrugerIds = [installationKey.ejendomNr.toString()];
                    const combinedKeys = forbrugerIds.map((id) => `${appServerUrl};${id}`);

                    // Aktiver notifikationer automatisk på app serveren
                    return this.pushNotificationService.requestNotificationPermissionAndRegisterOnAppServer(appServerUrl, combinedKeys);
                }),
                catchError((error) => {
                    console.error("Fejl ved automatisk aktivering af notifikationer:", error);
                    this.isCreatingSubscription = false;
                    return of(null);
                })
            )
            .subscribe({
                next: (result) => {
                    this.isCreatingSubscription = false;

                    if (result) {
                        // Opdater signal med notifikationsstatus
                        this.notificationsEnabledOnServer.set(true);
                    }
                },
                error: (error) => {
                    console.error("Fejl ved automatisk aktivering af notifikationer:", error);
                    this.isCreatingSubscription = false;
                }
            });
    }

    private createServerSubscription(enableNotifications: boolean): void {
        // FOF-brugere/teknikere skal IKKE subscribes til notifikationer
        if (this.isFofBruger()) {
            this.isLoading.set(false);
            this.hasRequestedPermission.set(true);
            this.shouldShowSession.set(false);
            return;
        }
        if (enableNotifications && !this.userInitiatedSubscriptionRequest) {
            console.warn("[PushNotification] blocked non-user-initiated subscription request");
            return;
        }
        if (enableNotifications) {
            this.userInitiatedSubscriptionRequest = false;
        }
        // Forhindre dobbelt kald
        if (this.isCreatingSubscription) {
            return;
        }

        this.isCreatingSubscription = true;

        // Hent forbrugernumre og forsyningNodeId fra aktuel installation og opdater subscription status
        combineLatest([this.aktuelInstallationKeyObserver.valueChanges, this.umbracoIndstillingerObserver.valueChanges])
            .pipe(
                takeUntil(this.destroy$),
                take(1),
                switchMap(([installationKey, umbracoIndstillinger]) => {
                    if (!installationKey || !umbracoIndstillinger?.aktuelEforsyning) {
                        this.isLoading.set(false);
                        this.isCreatingSubscription = false; // Reset flag ved fejl
                        this.hasRequestedPermission.set(false);
                        return of(null);
                    }

                    const forbrugerIds = [installationKey.ejendomNr.toString()];
                    const appServerUrl = umbracoIndstillinger.aktuelEforsyning.appServerUrl;

                    // Valider at vi har de nødvendige data
                    if (!installationKey.ejendomNr || !appServerUrl) {
                        console.error("❌ Mangler nødvendige data for at oprette combinedKeys");
                        this.isLoading.set(false);
                        this.isCreatingSubscription = false; // Reset flag ved fejl
                        this.hasRequestedPermission.set(false);
                        return of(null);
                    }

                    // Opret kombineret nøgle: "appServerUrl;forbrugernr"
                    const combinedKeys = forbrugerIds.map((id) => `${appServerUrl};${id}`);

                    if (enableNotifications) {
                        // Aktiver notifikationer - send direkte til app serveren
                        return this.pushNotificationService.requestNotificationPermissionAndRegisterOnAppServer(appServerUrl, combinedKeys);
                    } else {
                        // Bloker notifikationer - send direkte til app serveren
                        const subscriptionData = this.pushNotificationService.getCurrentSubscription();
                        const userId = this.pushNotificationService.getCurrentUserId();
                        if (userId && appServerUrl) {
                            return this.pushNotificationService.unregisterSubscriptionFromAppServer(appServerUrl, userId).pipe(
                                map(() => true),
                                catchError((error) => {
                                    console.error("Fejl ved afmelding fra app server:", error);
                                    return of(true); // Returner success selv ved fejl
                                })
                            );
                        } else {
                            return of(true); // Returner success for at undgå fejl
                        }
                    }
                }),
                catchError((error) => {
                    console.error("Fejl ved opdatering af notifikations-status:", error);
                    const action = enableNotifications ? "aktivering" : "blokering";
                    this.toastService.visFejl(`Der opstod en fejl ved ${action} af notifikationer`);
                    this.isLoading.set(false);
                    this.isCreatingSubscription = false; // Reset flag ved fejl
                    this.hasRequestedPermission.set(false);
                    return of(null);
                })
            )
            .subscribe({
                next: (result) => {
                    this.clearSubscriptionTimeout();
                    this.isLoading.set(false);
                    this.isCreatingSubscription = false; // Reset flag

                    if (result) {
                        // Husk om notifikationer var aktiveret FØR - vis kun toast ved faktisk ændring.
                        // Tjek FØR vi opdaterer notificationsEnabledOnServer.
                        const wasAlreadyEnabled = this.notificationsEnabledOnServer() === true;

                        // Opdater signal med notifikationsstatus
                        this.notificationsEnabledOnServer.set(enableNotifications);
                        // Fjern sessionStorage og skjul popup når subscription er oprettet/fjernet
                        sessionStorage.removeItem("notification-permission-shown");
                        this.shouldShowSession.set(false);
                        // Sæt hasRequestedPermission til true så popup'en ikke vises igen
                        this.hasRequestedPermission.set(true);
                        this.cdr.detectChanges();

                        if (enableNotifications && this.isUserLoggedIn()) {
                            this.saveSubscriptionOwnerKey();
                            // Vis KUN toast ved faktisk aktivering - aldrig ved re-registrering eller når allerede aktiveret
                            if (!wasAlreadyEnabled) {
                                this.toastService.visInfo("Notifikationer er nu aktiveret!");
                            }
                        } else if (this.isUserLoggedIn()) {
                            this.toastService.visInfo("Notifikationer er nu blokeret");
                        }
                    } else {
                        this.hasRequestedPermission.set(false);
                        const action = enableNotifications ? "aktivere" : "blokere";
                        if (this.isUserLoggedIn()) {
                            this.toastService.visFejl(`Kunne ikke ${action} notifikationer. Prøv venligst igen.`);
                        }
                        // Vis ALDRIG popup lokalt baseret på heuristik her.
                        // Popup skal kun styres af status-check flowet i ngOnInit, efter serverstatus er kendt.
                        this.shouldShowSession.set(false);
                    }
                },
                error: (error) => {
                    console.error("Fejl i subscription:", error);
                    this.clearSubscriptionTimeout();
                    this.isLoading.set(false);
                    this.isCreatingSubscription = false; // Reset flag ved fejl
                    this.hasRequestedPermission.set(false);
                    const action = enableNotifications ? "aktivering" : "blokering";
                    if (this.isUserLoggedIn()) {
                        this.toastService.visFejl(`Der opstod en fejl ved ${action} af notifikationer`);
                    }
                }
            });

        // Tilføj timeout som backup
        this.clearSubscriptionTimeout();
        this.subscriptionTimeoutHandle = setTimeout(() => {
            if (this.isLoading()) {
                this.isLoading.set(false);
                this.isCreatingSubscription = false; // Reset flag ved timeout
                this.hasRequestedPermission.set(false);
                if (this.isUserLoggedIn()) {
                    const action = enableNotifications ? "aktivering" : "blokering";
                    this.toastService.visFejl(`${action} af notifikationer tog for lang tid. Prøv venligst igen.`);
                }
            }
        }, 10000);
    }

    // dismissRequest() er fjernet - brugeren skal nu vælge enten "Tillad notifikationer" eller "Slå fra permanent"
    // Metoden er beholdt for bagudkompatibilitet, men kaldes ikke længere fra UI
    public dismissRequest(): void {
        // Denne metode er ikke længere i brug - brugeren skal vælge enten at tillade eller blokere
        // Hvis der er behov for at lukke popup'en uden at vælge, kan brugeren klikke "Slå fra permanent"
        this.blockNotifications();
    }

    public blockNotifications(): void {
        this.isLoading.set(true);
        this.hasRequestedPermission.set(true);

        // Marker som permanent afvist i localStorage (varer over sessions)
        localStorage.setItem(this.getPermanentDismissalKey(), "true");

        // Bloker notifikationer på serveren
        this.createServerSubscription(false);
    }

    public cancelOverwriteRequest(): void {
        // Luk popup uden at ændre subscription
        sessionStorage.removeItem("notification-permission-shown");
        this.shouldShowSession.set(false);
        this.shouldOverwriteExistingSubscription.set(false);
        this.hasRequestedPermission.set(true);
        this.cdr.detectChanges();
    }

    // Tilføj en metode til at nulstille permanent afvisning (til debugging eller admin)
    public resetPermanentDismissal(): void {
        localStorage.removeItem(this.getPermanentDismissalKey());
        localStorage.removeItem("notification-permission-permanently-dismissed");
        this.hasRequestedPermission.set(false);
        this.hasDismissed.set(false);
        sessionStorage.removeItem("notification-permission-shown");
        this.shouldShowSession.set(false);
    }

    private getSubscriptionOwnerKey(): string | null {
        return localStorage.getItem("push-subscription-installation-key");
    }

    private saveSubscriptionOwnerKey(): void {
        if (!this.currentInstallationKey) {
            return;
        }
        localStorage.setItem("push-subscription-installation-key", this.currentInstallationKey);
    }

    private getPermanentDismissalKey(): string {
        return this.currentInstallationKey
            ? `notification-permission-permanently-dismissed::${this.currentInstallationKey}`
            : "notification-permission-permanently-dismissed";
    }

    private clearSubscriptionTimeout(): void {
        if (this.subscriptionTimeoutHandle) {
            clearTimeout(this.subscriptionTimeoutHandle);
            this.subscriptionTimeoutHandle = null;
        }
    }

    private resetPendingSubscriptionState(): void {
        this.clearSubscriptionTimeout();
        this.isLoading.set(false);
        this.isCreatingSubscription = false;
        this.hasRequestedPermission.set(false);
    }

    /**
     * Tjekker om den logged-in bruger er FOF (rolle 9).
     * Bruger sessionStorage "user" sat ved login - ikke aktuelBrugerObserver, da den returnerer
     * installation-kontekst rolle (f.eks. Forbruger når FOF viewer en forbrugers installation).
     */
    private erLoggetIndSomFofBruger(): boolean {
        try {
            const userJson = this.sessionStorage.getItem("user");
            if (!userJson) return false;
            const user = JSON.parse(userJson) as { rolle?: number };
            return user?.rolle === Rolle.FofBruger;
        } catch {
            return false;
        }
    }
}
